import { mdiArrowDownDropCircleOutline, mdiArrowRightDropCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { TbFileSearch } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { Dimmer, Grid, Loader, Message, Placeholder, Table, TableRow } from "semantic-ui-react";

import { Recipe, useDeleteRecipe, useGetRecipeList } from "../../api/recipeSimulator";
import PlusIcon from "../../assets/commonIcon/PlusIcon";
import { CommonTable, CustomButton, InputText, TitleBar } from "../../components";
import ConfirmModal from "../../components/confirmViewModal/ConfirmModal";
import PaginationView from "../../components/pagination/Pagination";
import AddRecipeModal from "../../components/recipeSimulator/AddrecipeModal";
import { RecipeSimulatorTableColView } from "../../config/constants";
import { warningMessage } from "../../helpers/ErrorHandler";
import { needsRecalculation } from "../../helpers/RecipeSimulatorHelper";
import { getImpactChangePercentage } from "../../utils/utils";

import "./RecipeSimulatorList.scss";

const RecipeSimulatorListView = () => {
  const navigate = useNavigate();
  const [showDeleteRecipeModal, setShowDeleteRecipeModal] = useState(false);
  const [isVisibleRecipeAddModal, setIsVisibleRecipeAddModal] = useState<boolean>(false);
  const [deleteRecipeData, setDeleterecipeData] = useState<any>({});
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [searchKey, setSearchKey] = useState("");

  const { register, watch } = useForm();

  const [searchRecipe] = watch(["searchRecipe"]);

  const {
    data: recipeSimulatorList,
    isLoading: isRecipeListLoading,
    refetch: refetchRecipeList
  } = useGetRecipeList(pageNumber, pageLimit, searchRecipe);

  const { isLoading: isRecipeDeleteLoading, mutate: deleteRecipe } = useDeleteRecipe();

  useEffect(() => {
    setPageNumber(1);
  }, [searchRecipe]);

  const handlePagination = (currentPage: number) => {
    setPageNumber(currentPage);
  };

  const handlePageSize = (pageSize: number) => {
    setPageLimit(pageSize);
  };

  const handleIngredientDelete = () => {
    deleteRecipe(deleteRecipeData, {
      onSuccess() {
        setDeleterecipeData({});
        refetchRecipeList();
      }
    });
    setShowDeleteRecipeModal(false);
  };

  const handleRowClick = (rowId: number, hasMultipleVariants: boolean) => {
    if (!hasMultipleVariants) {
      warningMessage("No additional variants to expand");
      return;
    }
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter(id => id !== rowId)
      : currentExpandedRows.concat(rowId);
    setExpandedRows(newExpandedRows);
  };

  const renderItemCaret = (rowId: number) => {
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    if (isRowCurrentlyExpanded) {
      return mdiArrowDownDropCircleOutline;
    } else {
      return mdiArrowRightDropCircleOutline;
    }
  };

  const loadTableData = () => {
    if (isRecipeListLoading) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <Table.Cell colSpan={7}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </TableRow>
          ))}
        </>
      );
    }
    const recipeData = recipeSimulatorList?.result?.map((recipe: Recipe) => {
      const originalImpact = recipe?.originalRecipeclimateChange || 0;
      return {
        recipe_id: recipe?._id,
        recipe_name: recipe?.recipeName,
        variants:
          recipe?.variants?.map(variant => ({
            ...variant,
            simulated_impact: variant.variantclimateChange || 0,
            impact_reduction_percentage: originalImpact
              ? getImpactChangePercentage(originalImpact, variant.variantclimateChange ?? 0)
              : "0.0000"
          })) || [],
        original_impact: originalImpact,
        last_updated: recipe?.updatedAt || null,
        last_calculated: recipe?.calculatedAt || null
      };
    });

    if (!recipeData || recipeData?.length === 0) {
      return (
        <TableRow>
          <Table.Cell colSpan={8} textAlign="center">
            <Message info>
              <Message.Header>No result found</Message.Header>
            </Message>
          </Table.Cell>
        </TableRow>
      );
    }

    return (
      <>
        {recipeData?.map((dataView: any, index: number) => {
          const isNeedToCalculate = needsRecalculation(dataView);
          return (
            <>
              <TableRow key={index} className={isNeedToCalculate ? "needs-recalculation" : ""}>
                <Table.Cell
                  onClick={() =>
                    handleRowClick(index, dataView.variants && dataView.variants.length > 1)
                  }
                >
                  <Icon
                    className="table-icon"
                    color="var(--tableEditIcon)"
                    path={renderItemCaret(index)}
                    size={1.2}
                  />
                </Table.Cell>
                <Table.Cell>{dataView?.recipe_name}</Table.Cell>
                <Table.Cell>
                  {dataView.variants && dataView.variants.length === 1 ? (
                    dataView.variants[0].variantName
                  ) : dataView.variants && dataView.variants.length > 1 ? (
                    <p onClick={() => handleRowClick(index, true)} className="variantLink">
                      Multiple
                    </p>
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {dataView.variants && dataView.variants.length === 1
                    ? dataView.original_impact.toFixed(4)
                    : ""}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {dataView.variants && dataView.variants.length === 1
                    ? dataView.variants[0].simulated_impact.toFixed(4)
                    : ""}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {dataView.variants && dataView.variants.length === 1
                    ? `${dataView.variants[0].impact_reduction_percentage}`
                    : ""}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {dataView.variants && dataView.variants.length === 1
                    ? `${
                        dataView?.last_updated
                          ? new Date(dataView.last_updated).toLocaleDateString()
                          : ""
                      }`
                    : ""}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {dataView.variants && dataView.variants.length === 1
                    ? `${
                        dataView?.last_updated
                          ? new Date(dataView.last_calculated).toLocaleDateString()
                          : ""
                      }`
                    : ""}
                </Table.Cell>
                <Table.Cell>
                  <Grid>
                    <Grid.Column computer={4} tablet={16} mobile={16} />
                    <Grid.Column
                      computer={4}
                      tablet={16}
                      mobile={16}
                      className="align-content-right"
                    >
                      <TbFileSearch
                        cursor="pointer"
                        onClick={() =>
                          navigate(`/recipe-simulator/${dataView?.recipe_id}/manage`, {
                            state: { scrollToResults: true }
                          })
                        }
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={4}
                      tablet={16}
                      mobile={16}
                      className="align-content-center"
                    >
                      <MdModeEditOutline
                        cursor={"pointer"}
                        onClick={() => {
                          navigate(`/recipe-simulator/${dataView?.recipe_id}/manage`);
                        }}
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={4}
                      tablet={16}
                      mobile={16}
                      className="align-content-center"
                    >
                      <MdDeleteOutline
                        cursor={"pointer"}
                        onClick={() => {
                          setShowDeleteRecipeModal(true);
                          setDeleterecipeData(dataView?.recipe_id);
                        }}
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                  </Grid>
                </Table.Cell>
              </TableRow>
              {expandedRows.includes(index) &&
                dataView.variants &&
                dataView.variants.length > 1 && (
                  <>
                    {dataView.variants.map((variant: any, variantIndex: number) => {
                      const isNeedToCalculate = needsRecalculation(dataView);
                      return (
                        <TableRow
                          key={`${index}-${variantIndex}`}
                          className={isNeedToCalculate ? "needs-recalculation" : ""}
                        >
                          <Table.Cell />
                          <Table.Cell />
                          <Table.Cell>{variant.variantName}</Table.Cell>
                          <Table.Cell textAlign="right">
                            {dataView.original_impact.toFixed(4)}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {variant.simulated_impact.toFixed(4)}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {variant.impact_reduction_percentage}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {`${
                              dataView?.last_updated
                                ? new Date(dataView.last_updated).toLocaleDateString()
                                : ""
                            }`}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            {`${
                              dataView?.last_calculated
                                ? new Date(dataView.last_calculated).toLocaleDateString()
                                : ""
                            }`}
                          </Table.Cell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
            </>
          );
        })}
      </>
    );
  };

  if (isRecipeDeleteLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <TitleBar titleName={"Recipe Simulator"} />
      <Grid centered>
        <Grid.Column computer={4}>
          <InputText
            register={register}
            name="searchRecipe"
            placeholder="Search Recipe"
            defaultValues={searchKey}
            onChangeFunction={(e: any) => {
              setSearchKey(e.target.value);
            }}
          />
        </Grid.Column>
        <Grid.Column computer={12} textAlign="right">
          <CustomButton
            theme="green"
            title="Add New Recipe"
            onClick={() => {
              setIsVisibleRecipeAddModal(true);
            }}
            iconWithText={true}
            iconChild={<PlusIcon />}
          />
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable
            tableHeaderData={RecipeSimulatorTableColView}
            singleLine
            enableTableFooter={!!recipeSimulatorList?.result?.length}
            tableFooterCol="16"
            tableFooterChild={
              <PaginationView
                changePageSize={handlePageSize}
                pageSize={pageLimit}
                totalPages={
                  recipeSimulatorList?.total ? Math.ceil(recipeSimulatorList.total / pageLimit) : 1
                }
                currentPageNo={pageNumber}
                currentSelectPage={handlePagination}
              />
            }
          >
            {loadTableData()}
          </CommonTable>
        </Grid.Column>
      </Grid>

      <ConfirmModal
        viewModal={showDeleteRecipeModal}
        closeModal={() => {
          setShowDeleteRecipeModal(false);
        }}
        cancel={() => {
          setShowDeleteRecipeModal(false);
        }}
        approve={() => {
          handleIngredientDelete();
        }}
        title="Delete Recipe Simulator"
        subTitle="Are you sure you want to remove this recipe simulator?"
      />
      {isVisibleRecipeAddModal && (
        <AddRecipeModal
          closeModal={() => {
            setIsVisibleRecipeAddModal(false);
          }}
        />
      )}
    </>
  );
};

export default RecipeSimulatorListView;
